import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { APP_CONFIG } from '@config/app.config';
import { useWarehouses } from '@features/warehouses/hooks/useWarehouseOperations';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import { RuleOperator } from '@domain/inventory/enums/RuleOperator.enum';
import { RuleField } from '@domain/inventory/enums/RuleField.enum';
import { Connection } from '@domain/connection/models/Connection';
import { ClientType } from '@domain/connection/models/Connector';
import WooCommerceCredentialsForm from '@features/connections/woocommerce/components/WooCommerceCredentialsForm';
import WarehouseConnectionSelect from '../WarehouseConnectionSelect';
import { useWooCommerceConnector, useWooCommerceSync, useWooCommerceInventoryPreview } from '@features/connections/woocommerce/hooks/useWooCommerce';
import { InventoryPreview } from '@features/warehouses/components/inventory-preview/InventoryPreview';

interface WooCommerceWarehouseFormProps {
  onClose: () => void;
  onBack: () => void;
}

const WooCommerceWarehouseForm: React.FC<WooCommerceWarehouseFormProps> = ({
  onClose,
  onBack,
}) => {
  const queryClient = useQueryClient();
  const { createWarehouse, isCreating } = useWarehouses();
  const { connector } = useWooCommerceConnector();
  const { syncProducts } = useWooCommerceSync();

  const [form, setForm] = useState({
      name: 'WooCommerce',
      location: '',
  });
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [showCreateConnection, setShowCreateConnection] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const { inventory: inventoryPreview, isLoading: isLoadingPreview } = useWooCommerceInventoryPreview(selectedConnection?.id ?? '');

  const isLoading = isCreating || isSyncing;

  const handleSubmit = async () => {
    if (!selectedConnection) return;

    await createWarehouse({
      name: form.name,
      location: form.location,
      warehouseConnections: [
        {
          sourceType: WarehouseConnectionType.WOOCOMMERCE,
          mutationType: MutationType.SYNC,
          rules: [
            {
              operator: RuleOperator.EQUALS,
              field: RuleField.CONNECTION_ID,
              value: selectedConnection.id,
            },
          ],
        },
      ],
    });

    try {
      setIsSyncing(true);
      await syncProducts(selectedConnection.id);
    } finally {
      setIsSyncing(false);
    }

    onClose();
  };

  const handleNewConnection = (connection: Connection) => {
    setShowCreateConnection(false);
    queryClient.invalidateQueries({ queryKey: ['connections'] });
    setSelectedConnection(connection);
  };

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center">
            <IconButton onClick={onBack} color="inherit">
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" component="div">
              Sync with WooCommerce
            </Typography>
          </Stack>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ flex: 1 }}>
        <Stack gap={3} sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: 100, height: 60, borderRadius: 1, border: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 2 }}>
              <img src={connector?.image} alt="WooCommerce" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
            </Box>
            <Box>
              <Typography variant="h6" color="text.primary">
                Sync your inventory with WooCommerce
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Your WooCommerce inventory will be synced with your warehouse inventory.
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              Warehouse details
            </Typography>
            <Stack gap={2} direction="row">
              <TextField
                label="Warehouse name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Location"
                value={form.location}
                onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
                fullWidth
                size="small"
                placeholder="e.g., Amsterdam, Netherlands"
              />
            </Stack>
          </Stack>

          <WarehouseConnectionSelect
            connectorType={ClientType.WOOCOMMERCE}
            selectedConnection={selectedConnection}
            onSelect={setSelectedConnection}
            onCreateConnection={() => setShowCreateConnection(true)}
            connectorName="WooCommerce"
          />

          {selectedConnection && (
            <InventoryPreview
              inventory={inventoryPreview}
              isLoading={isLoadingPreview}
              emptyMessage="No inventory to show for this connection"
              title="Preview of warehouse inventory"
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedConnection || isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Create warehouse'}
        </Button>
      </DialogActions>

      <Dialog
        open={showCreateConnection}
        onClose={() => setShowCreateConnection(false)}
        maxWidth="sm"
      >
        <WooCommerceCredentialsForm
          onNewConnection={handleNewConnection}
          onClose={() => setShowCreateConnection(false)}
        />
      </Dialog>
    </>
  );
};

export default WooCommerceWarehouseForm;
